import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  getDeletesForSegmentPure,
  getPlayerTimeFromAbsoluteTimePure
} from '@goldcast/player-config/src/utils/timeUtils';
import {
  getEndForSegment,
  getStartForSegment,
  isClipInOriginalLanguage,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { roundToNDecimalPlaces } from '@goldcast/player-config/src/utils/mathUtils';
import { CompositeSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import DeleteItem from './DeleteItem';
import { useDragElement } from '../../hooks/useDragElement';
import { getPlayerTimeFromSegmentTime, getPreviousSegmentAbsoluteTime, MIN_SEGMENT_WIDTH } from '../../timeUtils';
import { updateClipData } from '../../clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '../../clipUpdateDataUtils/constants';
import { getMaxVideoDurationForSegment } from '../../playerConfigUtil';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { zoomLevelStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/zoomLevel';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { defaultTimelineSelectionState, transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import useThrottle from '@/hooks/useThrottle';

// A delete has no length in player time, so its start and end share one player time, and the
// cue point maps that exact time to the delete's end. After a start-handle drag, seeking just
// before it keeps the cue point on the delete's start edge; after an end-handle drag, the
// exact time keeps it on the end edge.
const DELETE_START_SEEK_OFFSET = 0.01;

export default function Deletes({
  compositeSegment,
  compositeSegmentDragStart,
  compositeSegmentDragEnd,
  newTimelineContainerRef,
  speakerTimelineContainerRef
}: {
  compositeSegment: CompositeSegment;
  compositeSegmentDragStart: number;
  compositeSegmentDragEnd: number;
  newTimelineContainerRef: React.RefObject<HTMLDivElement>;
  speakerTimelineContainerRef: React.RefObject<HTMLDivElement>;
}) {
  const { clipId, clipData, fps } = useClipsContext();
  const allDeletes = useMemo(
    () =>
      getDeletesForSegmentPure({
        clip: clipData,
        segmentId: compositeSegment.id,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      }),
    [compositeSegment, clipData]
  );
  const { seekTo, seekToAfterClipUpdate } = useRemotionPlayerSeek();
  const seekToDeleteStart = (deleteStartPlayerTime: number) =>
    seekToAfterClipUpdate(Math.max(deleteStartPlayerTime - DELETE_START_SEEK_OFFSET, 0));
  const [selectedStartDelete, setSelectedStartDelete] = useState<string | null>(null);
  const [selectedStartDeleteDrag, setSelectedStartDeleteDrag] = useState<number | null>(null);
  const [selectedEndDelete, setSelectedEndDelete] = useState<string | null>(null);
  const [selectedEndDeleteDrag, setSelectedEndDeleteDrag] = useState<number | null>(null);
  const { selectedDeleteIndex } = useSyncExternalStore(
    transcriptTimelineSelectionStore.subscribe,
    transcriptTimelineSelectionStore.getSnapshot
  );
  const isSplitEnabled = useMemo(
    () =>
      isSplitsInTimelineEnabledPure({
        clip: clipData,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      }),
    [clipData]
  );
  const onDragAbortRef = useRef<() => void>();
  const compositeStart = getStartForSegment({
    clip: clipData,
    segmentId: compositeSegment.id,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });
  const compositeEnd = getEndForSegment({
    clip: clipData,
    segmentId: compositeSegment.id,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });
  const compositeMaxDuration = getMaxVideoDurationForSegment(clipData, compositeSegment.id);
  const compositeStartOffsetTime = compositeStart + compositeSegmentDragStart;
  const compositeEndOffsetTime = compositeEnd + compositeSegmentDragEnd;

  const throttledForceScrollEvent = useThrottle(
    (time: number) =>
      EventBus.dispatch(CustomEvents.TimelineForcedScroll, {
        time,
        segmentId: compositeSegment.id
      }),
    100
  );

  const allowDeleteAdjustment = useMemo(() => {
    return isClipInOriginalLanguage(clipData);
  }, [clipData]);

  const selectedDeleteStartId = useMemo(() => {
    return selectedDeleteIndex !== null ? Object.keys(allDeletes)[selectedDeleteIndex] : null;
  }, [selectedDeleteIndex, allDeletes]);

  const { onMouseDown: onStartDeleteDrag } = useDragElement({
    setDraggingElement: (id: string | null) => {
      if (selectedDeleteStartId === id) {
        transcriptTimelineSelectionStore.update(state => ({
          ...state,
          isSelecting: true
        }));
      }
      setSelectedStartDelete(id);
    },
    setDraggingElementOffset: setSelectedStartDeleteDrag,
    onDragEnd: (offset, dragId) => {
      const newStartTime = roundToNDecimalPlaces(Math.max(allDeletes[dragId].bounds[0] + offset, 0), 3);
      const endTime = roundToNDecimalPlaces(allDeletes[dragId].bounds[1], 3);

      const newClipData = updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.UPDATE_COMPOSITE_SEGMENT_DELETE,
        deleteId: dragId,
        endTime: endTime,
        startTime: newStartTime,
        compositeId: compositeSegment.id
      });

      if (selectedDeleteStartId === dragId) {
        transcriptTimelineSelectionStore.update(state => ({
          ...state,
          isSelecting: false
        }));
      }
      const compositeSegmentStart = getStartForSegment({
        clip: newClipData,
        segmentId: compositeSegment.id,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });

      // Map with the updated deletes: the old delete still covers `newStartTime`, so the old
      // clip data would resolve to the old delete start instead of the new one.
      if (compositeSegmentStart > newStartTime) {
        seekToAfterClipUpdate(
          getPlayerTimeFromSegmentTime(compositeSegmentStart, newClipData, compositeSegment.id) + 0.01
        );
        return;
      }
      seekToDeleteStart(getPlayerTimeFromSegmentTime(newStartTime, newClipData, compositeSegment.id));
    },
    validDropZoneRef: newTimelineContainerRef,
    droppableContainerRef: speakerTimelineContainerRef,
    fps
  });

  const { onMouseDown: onEndDeleteDrag } = useDragElement({
    setDraggingElement: (id: string | null) => {
      if (selectedDeleteStartId === id) {
        transcriptTimelineSelectionStore.update(state => ({
          ...state,
          isSelecting: true
        }));
      }
      setSelectedEndDelete(id);
    },
    setDraggingElementOffset: setSelectedEndDeleteDrag,
    onDragEnd: (offset, dragId) => {
      const newEndTime = roundToNDecimalPlaces(Math.min(allDeletes[dragId].bounds[1] + offset, compositeEnd), 3);

      const oldStartTime = roundToNDecimalPlaces(allDeletes[dragId].bounds[0], 3);
      const newClipData = updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.UPDATE_COMPOSITE_SEGMENT_DELETE,
        deleteId: dragId,
        endTime: newEndTime,
        startTime: oldStartTime,
        compositeId: compositeSegment.id
      });

      if (selectedDeleteStartId === dragId) {
        transcriptTimelineSelectionStore.update(state => ({
          ...state,
          isSelecting: false
        }));

        if (newEndTime === compositeEnd) {
          transcriptTimelineSelectionStore.set(() => ({
            ...defaultTimelineSelectionState
          }));
        }
      }

      let seekCompositeTime = newEndTime;
      if (compositeEnd < newEndTime) {
        seekCompositeTime = compositeEnd;
      }

      seekToAfterClipUpdate(getPlayerTimeFromSegmentTime(seekCompositeTime, newClipData, compositeSegment.id));
    },
    validDropZoneRef: newTimelineContainerRef,
    droppableContainerRef: speakerTimelineContainerRef,
    fps
  });

  useEffect(() => {
    setSelectedStartDelete(null);
    setSelectedStartDeleteDrag(0);
    setSelectedEndDelete(null);
    setSelectedEndDeleteDrag(0);
    onDragAbortRef.current?.();
  }, [clipData.asset_metadata.deletes]);

  useEffect(() => {
    // Helper function to handle delete drag changes
    const handleDeleteDragChange = (
      selectedDelete: string | null,
      dragValue: number | null,
      isStartHandle: boolean
    ) => {
      if (!selectedDelete || !dragValue) {
        return;
      }

      const deleteBounds = allDeletes[selectedDelete].bounds;
      const baseTime = isStartHandle ? deleteBounds[0] : deleteBounds[1];
      const adjustedTime = baseTime + dragValue;

      const shouldAdd = (isStartHandle && dragValue < 0) || (!isStartHandle && dragValue > 0);
      const type = shouldAdd ? 'add' : 'remove';

      if (isStartHandle) {
        EventBus.dispatch(CustomEvents.TimelineDeleteChangeDuringDrag, {
          type: type,
          startTime: Math.min(adjustedTime, baseTime),
          endTime: Math.max(adjustedTime, baseTime),
          compositeSegmentId: compositeSegment.id
        });
      } else {
        EventBus.dispatch(CustomEvents.TimelineDeleteChangeDuringDrag, {
          type: type,
          startTime: Math.min(baseTime, adjustedTime),
          endTime: Math.max(baseTime, adjustedTime),
          compositeSegmentId: compositeSegment.id
        });
      }

      throttledForceScrollEvent(adjustedTime);
    };

    // Handle start delete drag
    handleDeleteDragChange(selectedStartDelete, selectedStartDeleteDrag, true);

    // Handle end delete drag
    handleDeleteDragChange(selectedEndDelete, selectedEndDeleteDrag, false);

    // Reset if no drag is happening
    if (!selectedStartDeleteDrag && !selectedEndDeleteDrag) {
      EventBus.dispatch(CustomEvents.TimelineDeleteChangeDuringDrag, {
        type: 'reset'
      });
    }
  }, [
    selectedStartDelete,
    selectedStartDeleteDrag,
    selectedEndDelete,
    selectedEndDeleteDrag,
    allDeletes,
    throttledForceScrollEvent,
    compositeSegment.id
  ]);

  const trimDelete = Object.values(allDeletes).find(item => item.bounds[0] === 0);

  return (
    <div className="pointer-events-none absolute inset-0 z-10">
      {Object.entries(allDeletes).map((entry, index) => {
        const deleteRange = [...entry[1].bounds];
        if (
          deleteRange[0] === 0 ||
          deleteRange[1] === compositeMaxDuration ||
          deleteRange[0] === deleteRange[1] ||
          deleteRange[1] <= compositeStartOffsetTime ||
          deleteRange[0] >= compositeEndOffsetTime
        ) {
          return null;
        }
        if (selectedStartDelete === entry[0] && selectedStartDeleteDrag !== null) {
          deleteRange[0] = Math.max(deleteRange[0] - compositeStartOffsetTime + selectedStartDeleteDrag, 0);
          deleteRange[1] = Math.min(compositeEndOffsetTime, Math.max(deleteRange[1] - compositeStartOffsetTime, 0));
        } else if (selectedEndDeleteDrag !== null && selectedEndDelete === entry[0]) {
          deleteRange[0] = Math.max(deleteRange[0] - compositeStartOffsetTime, 0);
          deleteRange[1] = Math.min(
            deleteRange[1] - compositeStartOffsetTime + selectedEndDeleteDrag,
            compositeEndOffsetTime - compositeStartOffsetTime
          );
        } else {
          deleteRange[0] = Math.max(deleteRange[0] - compositeStartOffsetTime, 0);
          deleteRange[1] = Math.min(compositeEndOffsetTime, Math.max(deleteRange[1] - compositeStartOffsetTime, 0));
        }
        const startDragMaxOffset = entry[1].bounds[1] - entry[1].bounds[0];
        const startDragMinOffset = entry[1].bounds[0] - compositeStart;
        const endDragMaxOffset = compositeEnd - entry[1].bounds[1];
        const endDragMinOffset = entry[1].bounds[1] - entry[1].bounds[0];

        const clickHandle = (e: React.MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
          const compositeOffset = getPreviousSegmentAbsoluteTime(compositeSegment, clipData);
          const trimOffset = compositeSegment.currentBounds.fromFrame / fps;
          const newSeekTime = getPlayerTimeFromAbsoluteTimePure({
            absoluteTime: entry[1].bounds[1] + compositeOffset - trimOffset,
            clipData,
            minSegmentWidth: MIN_SEGMENT_WIDTH,
            zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
            isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
          });
          seekTo(newSeekTime + 0.01);
          EventBus.dispatch(CustomEvents.TimelineForcedScroll, {
            time: entry[1].bounds[1],
            segmentId: compositeSegment.id
          });
          transcriptTimelineSelectionStore.set(() => ({
            ...defaultTimelineSelectionState,
            startTime: entry[1].bounds[0],
            startSegmentId: compositeSegment.id,
            endTime: entry[1].bounds[1],
            endSegmentId: compositeSegment.id,
            selectedDeleteIndex: index,
            selectedSegmentId: compositeSegment.id
          }));
        };
        return (
          <DeleteItem
            key={entry[0]}
            entry={entry}
            clickHandle={clickHandle}
            selectedStartDelete={selectedStartDelete}
            selectedEndDelete={selectedEndDelete}
            onStartDeleteDrag={onStartDeleteDrag}
            onEndDeleteDrag={onEndDeleteDrag}
            startDragMaxOffset={startDragMaxOffset}
            startDragMinOffset={startDragMinOffset}
            endDragMaxOffset={endDragMaxOffset}
            endDragMinOffset={endDragMinOffset}
            deleteRange={deleteRange}
            trimDelete={trimDelete}
            isSelected={selectedDeleteStartId === entry[0] && !isSplitEnabled}
            fps={fps}
            allowDeleteAdjustment={allowDeleteAdjustment}
          />
        );
      })}
    </div>
  );
}
